import { needAdditionalInfo } from '@shinetools/ape-code-library';
import { ERROR_CODES } from '@shinetools/errors';

import assertEventType from 'common/bento/lib/assertEventType';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import { getServerErrorCode } from 'common/errors/utils';
import getIsEi from 'features/Onboarding/lib/getIsEi';

import { ErrorCodes } from '../../../types';
import companyProfileStorage from '../companyProfileStorage';
import { Model } from './model';

export enum Guard {
  ApeCodeNeedsDetails = 'apeCodeNeedsDetails',
  IsIdentityInfoMissing = 'isIdentityInfoMissing',
  IsLegalInfoMissing = 'isLegalInfoMissing',
  IsLegalFormMissing = 'isLegalFormMissing',
  HasSelectedExternalCompany = 'hasSelectedExternalCompany',
  HasStoredCompanyInput = 'hasStoredCompanyInput',
  ShouldClarifyIsMicro = 'shouldClarifyIsMicro',
  HasCompanyNotSupportedError = 'hasCompanyNotSupportedError',
  IsReadyForValidation = 'isReadyForValidation',
}

const guards: MachineGuards<Model, Guard> = {
  apeCodeNeedsDetails: ({ companyProfile }, event) => {
    if (event.type === 'SUBMIT_LEGAL_INFO') {
      return needAdditionalInfo(event.data.activityType);
    }

    return needAdditionalInfo(companyProfile.activityType);
  },

  hasCompanyNotSupportedError: (context, event) => {
    assertEventType(event, 'error.platform.submitCompanyInfo');

    return [
      ERROR_CODES.COMPANY_REFUSED,
      ErrorCodes.COMPANY_NOT_SUPPORTED,
    ].includes(getServerErrorCode(event.data) as string);
  },

  hasSelectedExternalCompany: ({ companyProfile }) => companyProfile.isExternal,

  /**
   * We consider that resuming an onboarding when the user entered nothing else than his legalForm
   * has no value, therefore we want the bare minimum to be a `legalName`.
   */
  hasStoredCompanyInput: () => {
    const company = companyProfileStorage.get();

    return Boolean(company && company.legalName);
  },

  isIdentityInfoMissing: ({ companyProfile }) => {
    const { city, legalName, street, zip } = companyProfile;
    return !legalName || !street || !zip || !street || !city;
  },

  isLegalFormMissing: ({ companyProfile }) => !companyProfile.legalForm,

  isLegalInfoMissing: ({ companyProfile }) => {
    const { activityType, legalRegistrationNumber, registrationDate } =
      companyProfile;
    return !activityType || !legalRegistrationNumber || !registrationDate;
  },

  isReadyForValidation: ({ companyProfile }) => {
    const {
      activityType,
      isExternal,
      isMicro,
      legalForm,
      legalName,
      legalRegistrationNumber,
      registrationDate,
    } = companyProfile;

    const isMissingBaseInfo =
      !legalForm ||
      !legalRegistrationNumber ||
      !legalName ||
      !registrationDate ||
      !activityType;

    if (isMissingBaseInfo) {
      return false;
    }

    // In the auto flow, the financial info page is after verification
    if (isExternal) {
      return true;
    }

    const {
      legalAnnualTurnOver,
      legalNumberOfEmployeeRange,
      legalShareCapital,
    } = companyProfile;

    if (!isMicro) {
      const isMissingNonMicroInfo =
        !legalNumberOfEmployeeRange || !legalAnnualTurnOver;

      if (isMissingNonMicroInfo) {
        return false;
      }
    }

    if (!getIsEi({ legalForm })) {
      const isMissingNonEIInfo = !legalShareCapital;

      if (isMissingNonEIInfo) {
        return false;
      }
    }

    return true;
  },

  shouldClarifyIsMicro: ({ companyProfile }) =>
    getIsEi(companyProfile) && companyProfile.isMicro === null,
};

export default guards;
